
import { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { usePageViewTracking } from './tracking/usePageViewTracking';
import { useAuth } from '@/contexts/auth';
import { PageViewTrackingProps } from './tracking/types';

/**
 * Component that tracks page views for a shop
 * ABSOLUTE PRIORITY: This event MUST be triggered BEFORE ViewContent
 * This component is responsible for sending the PageView event and signaling its completion
 */
export const PageViewTracker: React.FC<PageViewTrackingProps> = ({ 
  children, 
  shopId: propShopId,
  organizationId: propOrganizationId
}) => {
  // Get shop ID from props or params
  const params = useParams<{ shopId?: string, shopSlug?: string }>();
  const location = useLocation();
  const effectiveShopId = propShopId || params.shopId || null;
  
  // Get organization ID from props or auth context
  const auth = useAuth();
  const effectiveOrgId = propOrganizationId || auth.currentOrganization?.id || null;
  
  // Check if current path is a shop path
  const isShopPath = location.pathname.includes('/shop/') || 
                     (location.pathname.match(/^\/[^\/]+\/products\//) !== null);
  
  // Debug log with timestamp to trace event sequence
  useEffect(() => {
    if (!isShopPath) {
      console.log("🔍 Path is not a shop path, PageViewTracker will not track:", location.pathname);
      return;
    }
    
    console.log("🔍 PageViewTracker mounted with:", { 
      shopId: effectiveShopId, 
      organizationId: effectiveOrgId,
      path: location.pathname,
      timestamp: new Date().toISOString(),
      sequence: "TRACKING_INIT"
    });
    
    // Log window.dataLayer availability for debugging
    if (typeof window !== 'undefined') {
      console.log("🔍 DataLayer availability:", {
        exists: !!window.dataLayer,
        type: window.dataLayer ? typeof window.dataLayer : 'undefined',
        isArray: window.dataLayer ? Array.isArray(window.dataLayer) : false
      });
      
      // Initialize dataLayer if it doesn't exist
      if (!window.dataLayer) {
        window.dataLayer = [];
        console.log("🔍 DataLayer initialized");
      }
      
      // Check if Meta Pixel is available
      console.log("🔍 Meta Pixel availability:", {
        exists: typeof window.fbq === 'function',
        type: window.fbq ? typeof window.fbq : 'undefined',
        hasGetState: window.fbq && typeof window.fbq.getState === 'function',
        initialized: window.fbq && window.fbq._initialized
      });

      // DEBUG: Check if window._fbq exists (old implementation)
      if (window._fbq) {
        console.log("🔍 Legacy _fbq exists:", window._fbq);
      }
    }

    // Check if fbq is loading but not available yet
    const checkFbqInterval = setInterval(() => {
      if (typeof window !== 'undefined' && typeof window.fbq === 'function' && !window._fbq_called) {
        console.log("🔍 Meta Pixel became available after component mount");
        window._fbq_called = true; // Mark that we've seen this
        
        // Send PageView if Meta Pixel just loaded
        try {
          console.log("Sending PageView to newly loaded Meta Pixel");
          window.fbq('track', 'PageView', {
            content_name: document.title,
            content_category: 'page',
            content_type: 'product'
          });
          console.log("PageView sent to newly loaded Meta Pixel");
        } catch (error) {
          console.error("Error sending PageView to newly loaded Meta Pixel:", error);
        }
        
        clearInterval(checkFbqInterval);
      }
    }, 500);

    return () => {
      clearInterval(checkFbqInterval);
    };
  }, [effectiveShopId, effectiveOrgId, location.pathname, isShopPath]);
  
  // Initialize page view tracking - ABSOLUTE PRIORITY
  // Only if we're on a shop path
  if (isShopPath) {
    usePageViewTracking({
      shopId: effectiveShopId, 
      organizationId: effectiveOrgId
    });
  }
  
  return <>{children}</>;
};

export default PageViewTracker;
